import { format } from "date-fns";
import { truncate } from "lodash-es";

import { serverGetBugReportSignedUrl, serverSubmitBugReport } from "api/api-server-actions";
import { getUserFullName } from "utils/user-utils";

const DIAGNOSTICS_FILENAME = "bug-report-diagnostics.json";

// Helper to build the bug report subject
function getBugReportSubject({
  orgName,
  description,
  user,
  currentUserId,
  currentSectionId,
  classSession,
  date,
}) {
  let subject = `[${orgName}]`;
  subject += ` ${truncate(description, { length: 120 }) || "Empty bug report"} --`;
  subject += ` ${getUserFullName(user)} (userId: ${currentUserId})`;
  subject += currentSectionId ? ` [sectionId: ${currentSectionId}]` : "";
  subject += classSession?.id
    ? ` [classSessionId: ${classSession?.id} (${classSession?.category || "none"})]`
    : "";
  subject += ` (${format(date, "M/d HH:mm xx")})`;
  return subject;
}

function getBugReportBody({
  description,
  fileUrls,
  user,
  classSession,
  currentSectionId,
  orgName,
  date,
}) {
  let report = [
    `${getBugReportSubject({
      orgName,
      description,
      user,
      currentUserId: user.id,
      currentSectionId,
      classSession,
      date,
    })}`,
    document.URL,
    navigator.userAgent,
    `Description:\n${description}`,
    `Diagnostics:\nSee attached ${DIAGNOSTICS_FILENAME}`,
  ].join("\n\n");

  if (fileUrls && fileUrls.length > 0) {
    report += `\n\nUser submitted the following files:\n${fileUrls.join("\n\n")}`;
  }

  return report;
}

function getBugReportDiagnostics({
  user,
  classUser,
  classSession,
  activePageId,
  presentationState,
  avState,
  date,
}) {
  return {
    schemaVersion: 1,
    capturedAt: date.toISOString(),
    pageUrl: document.URL,
    userAgent: navigator.userAgent,
    user,
    classUser: classUser ?? null,
    classSession: classSession ?? null,
    presentationState: presentationState ?? null,
    activePageId: activePageId ?? null,
    avState: avState ?? null,
  };
}

export async function submitBugReportHelper({
  description,
  attachedFiles,
  user,
  classUser,
  classSession,
  currentSectionId,
  activePageId,
  presentationState,
  orgName,
  avState,
}) {
  const date = new Date();

  // Upload files to S3 and get URLs
  const fileUrls: string[] = [];

  if (attachedFiles.length > 0) {
    const uploadPromises = attachedFiles.map(async (file, index) => {
      try {
        // Step 1: Get pre-signed URL for this file
        const fileExtension = file.name.includes(".") ? `.${file.name.split(".").pop()}` : "";
        const fileName = `file${index + 1}${fileExtension}`;
        const { upload_url, file_url } = await serverGetBugReportSignedUrl(fileName, file.type);

        // Step 2: Upload file to S3
        const uploadResponse = await fetch(upload_url, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload ${file.name} to S3: ${uploadResponse.statusText}`);
        }

        // Step 3: Return the file URL
        return file_url;
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        // Return null for failed uploads
        return null;
      }
    });

    // Wait for all uploads to complete
    const results = await Promise.all(uploadPromises);
    console.info("File upload results:", results);
    // Filter out failed uploads (null values)
    fileUrls.push(...results.filter((url): url is string => url !== null));
  }

  // Submit bug report with file URLs and structured diagnostics
  await serverSubmitBugReport({
    subject: getBugReportSubject({
      orgName,
      description,
      user,
      currentUserId: user.id,
      currentSectionId,
      classSession,
      date,
    }),
    description: getBugReportBody({
      description,
      fileUrls,
      user,
      classSession,
      currentSectionId,
      orgName,
      date,
    }),
    diagnostics: getBugReportDiagnostics({
      user,
      classUser,
      classSession,
      activePageId,
      presentationState,
      avState,
      date,
    }),
    section_id: currentSectionId || null,
    class_session_id: classSession?.id || null,
    submission_url: document.URL,
    user_uploaded_files_urls: fileUrls.length > 0 ? fileUrls : null,
  });
}
